import { createSlice } from '@reduxjs/toolkit';

// utils
import axios, { endpoints } from '../../utils/axios';
import { trackEvent, ANALYTICS_EVENTS } from '../../utils/posthog';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  analysisResult: null,
  batchId: null,
  batchStatus: null,
};

const slice = createSlice({
  name: 'bulkCrossList',
  initialState,
  reducers: {
    // START LOADING
    startLoading (state) {
      state.isLoading = true;
    },
    // END LOADING
    endLoading (state) {
      state.isLoading = false;
    },
    // HAS ERROR
    hasError (state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    // RESET ERROR
    resetError (state) {
      state.isLoading = false;
      state.error = null;
    },
    // ANALYSIS SUCCESS
    analyzeSuccess (state, action) {
      state.analysisResult = action.payload;
      state.isLoading = false;
    },
    // EXECUTE SUCCESS
    executeSuccess (state, action) {
      state.batchId = action.payload.batchId;
      state.isLoading = false;
    },
    // BATCH STATUS SUCCESS
    batchStatusSuccess (state, action) {
      state.batchStatus = action.payload;
      state.isLoading = false;
    },
    // RESET STATE
    resetState (state) {
      state.analysisResult = null;
      state.batchId = null;
      state.batchStatus = null;
      state.error = null;
    }
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function analyzeListings (listingIds, targetPlatforms, globalDefaults = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    trackEvent(ANALYTICS_EVENTS.CROSSLIST_ANALYZE_STARTED, {
      listingCount: Array.isArray(listingIds) ? listingIds.length : undefined,
      targetPlatformCount: Array.isArray(targetPlatforms) ? targetPlatforms.length : undefined,
    });
    try {
      const response = await axios.post(endpoints.bulkCrossList.analyze, {
        listingIds,
        targetPlatforms,
        globalDefaults // Pass for eBay/Etsy validation
      });

      dispatch(slice.actions.analyzeSuccess(response.data.analysis));
      trackEvent(ANALYTICS_EVENTS.CROSSLIST_ANALYZE_COMPLETED, {
        listingCount: Array.isArray(listingIds) ? listingIds.length : undefined,
        targetPlatformCount: Array.isArray(targetPlatforms) ? targetPlatforms.length : undefined,
        readyCount: Array.isArray(response.data?.analysis?.ready) ? response.data.analysis.ready.length : undefined,
        needsAttentionCount: Array.isArray(response.data?.analysis?.needsAttention) ? response.data.analysis.needsAttention.length : undefined,
      });
      return response.data;
    } catch (error) {
      console.error('Analyze listings error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to analyze listings'));
      trackEvent(ANALYTICS_EVENTS.CROSSLIST_ANALYZE_FAILED, {
        listingCount: Array.isArray(listingIds) ? listingIds.length : undefined,
        targetPlatformCount: Array.isArray(targetPlatforms) ? targetPlatforms.length : undefined,
      });
      throw error;
    }
  };
}

export function executeBulkCrossListing (listingIds, targetPlatforms, userMappings, targetAccounts = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    trackEvent(ANALYTICS_EVENTS.CROSSLIST_EXECUTE_SUBMITTED, {
      listingCount: Array.isArray(listingIds) ? listingIds.length : undefined,
      targetPlatformCount: Array.isArray(targetPlatforms) ? targetPlatforms.length : undefined,
    });
    try {
      const response = await axios.post(endpoints.bulkCrossList.execute, {
        listingIds,
        targetPlatforms,
        userMappings: userMappings || {},
        targetAccounts
      });

      dispatch(slice.actions.executeSuccess(response.data));
      return response.data;
    } catch (error) {
      console.error('Execute bulk cross-listing error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to execute bulk cross-listing'));
      throw error;
    }
  };
}

// `silent` is for looking up a run that is only being read, not followed: a completed batch the
// user opens later is not a batch completing, and must not be reported as one.
export function getBatchStatus (batchId, { silent = false } = {}) {
  return async (dispatch) => {
    try {
      const response = await axios.get(endpoints.bulkCrossList.status(batchId));
      dispatch(slice.actions.batchStatusSuccess(response.data.status));
      if (!silent && response.data?.status?.status === 'completed') {
        trackEvent(ANALYTICS_EVENTS.CROSSLIST_BATCH_COMPLETED, {
          success: response.data.status?.completed ?? 0,
          failed: response.data.status?.failed ?? 0,
        });
      }
      return response.data;
    } catch (error) {
      console.error('Get batch status error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to get batch status'));
      throw error;
    }
  };
}

export function resetBulkCrossListState () {
  return (dispatch) => {
    dispatch(slice.actions.resetState());
  };
}

